/**
 * Design tokens — single source of truth for all premium scenes.
 *
 * Rule: scenes must NEVER hardcode colors, sizes, weights, radii, or shadows.
 * Always read from `tokens` (numerics) or `useBrand()` (colors / fonts).
 */
import type { FormatId } from "../hyperframes/format.js";

export type TypeStep = "xs" | "sm" | "md" | "lg" | "xl" | "hero" | "mega";

/**
 * Type scale. Values are in CSS pixels (Remotion's native unit).
 * Tuned per format so a "hero" looks heroic in 9:16 but doesn't blow out 16:9.
 */
export const typeScale: Record<TypeStep, Record<FormatId, number>> = {
  xs: { "9:16": 28, "1:1": 26, "16:9": 26 },
  sm: { "9:16": 38, "1:1": 36, "16:9": 36 },
  md: { "9:16": 56, "1:1": 50, "16:9": 50 },
  lg: { "9:16": 84, "1:1": 72, "16:9": 76 },
  xl: { "9:16": 120, "1:1": 100, "16:9": 110 },
  hero: { "9:16": 170, "1:1": 140, "16:9": 150 },
  mega: { "9:16": 240, "1:1": 200, "16:9": 220 },
};

/** Font weights (only these three are allowed for consistency). */
export const weights = {
  regular: 400,
  semibold: 600,
  black: 800,
} as const;

/** Spacing scale (CSS pixels). */
export const space = {
  xxs: 4,
  xs: 8,
  sm: 16,
  md: 24,
  lg: 40,
  xl: 64,
  xxl: 96,
  huge: 144,
} as const;

/** Corner radii. */
export const radii = {
  sm: 16,
  md: 24,
  lg: 40,
  xl: 56,
  pill: 9999,
} as const;

/** Letter-spacing presets — bold display type wants tight tracking. */
export const tracking = {
  hero: -3.5,
  display: -2,
  /** Alias of display — used by some scene files; keep both for compatibility. */
  heading: -2,
  body: -0.4,
  caps: 4,
} as const;

/** Line-heights. */
export const leading = {
  display: 0.95,
  heading: 1.05,
  body: 1.3,
} as const;

/** Safe-area padding per format. */
export const safePad: Record<FormatId, number> = {
  "9:16": 88,
  "1:1": 80,
  "16:9": 110,
};

/** Returns a soft drop shadow tinted with the given color. */
export function softShadow(rgb: string, opacity = 0.35): string {
  return `0 24px 64px ${withAlpha(rgb, opacity)}`;
}

/** Returns an elevated card shadow. */
export function elevatedShadow(rgb: string, opacity = 0.5): string {
  return `0 32px 96px ${withAlpha(rgb, opacity)}, 0 8px 24px ${withAlpha(rgb, opacity * 0.4)}`;
}

/** Returns a colored glow for buttons / hero text. */
export function glow(rgb: string, opacity = 0.55): string {
  return `0 0 80px ${withAlpha(rgb, opacity)}, 0 0 24px ${withAlpha(rgb, opacity * 0.6)}`;
}

/**
 * Append an alpha hex byte to a hex color (e.g. "#04b4b4" + 0.5 → "#04b4b480").
 * Falls back gracefully if the input isn't a 6-digit hex.
 */
export function withAlpha(hex: string, opacity: number): string {
  const clamped = Math.max(0, Math.min(1, opacity));
  const byte = Math.round(clamped * 255)
    .toString(16)
    .padStart(2, "0");
  if (/^#[0-9a-f]{6}$/i.test(hex)) return `${hex}${byte}`;
  if (/^#[0-9a-f]{3}$/i.test(hex)) {
    const r = hex[1]!;
    const g = hex[2]!;
    const b = hex[3]!;
    return `#${r}${r}${g}${g}${b}${b}${byte}`;
  }
  return hex;
}

/** Spring presets — never hand-roll spring config in scenes. */
export const springs = {
  punchy: { damping: 14, stiffness: 220, mass: 0.8 },
  smooth: { damping: 20, stiffness: 130, mass: 1 },
  snappy: { damping: 18, stiffness: 260, mass: 0.7 },
  soft: { damping: 26, stiffness: 90, mass: 1.1 },
} as const;

export type SpringPreset = keyof typeof springs;
